import { FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import { Section } from "components/Section";
import { NumberField } from "components/NumberField";
import getStyles from "../utils/styles";

type Props = {
  side: "left" | "right";
  areaWidth?: number;
  kerbDistance?: number;
  onInput: (name: string, value: number) => void;
};

const BorderArea: FC<Props> = ({ side, areaWidth, kerbDistance, onInput }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).BorderArea, [theme]);

  const { title, areaWidthName, kerbDistanceName, tooltip } = useMemo(
    () => ({
      title: side === "left" ? t("Border area left") : t("Border area right"),
      areaWidthName: side === "left" ? "road_width_border_area_left" : "road_width_border_area_right",
      kerbDistanceName: side === "left" ? "kerb_distance_left" : "kerb_distance_right",
      tooltip: side === "left" ? t("Kerb distance left") : t("Kerb distance right"),
    }),
    [side, t],
  );

  return (
    <Section title={title} sx={styles.area}>
      <NumberField
        name={areaWidthName}
        value={areaWidth}
        label={`${t("Width")} (m)`}
        min={0.01}
        max={25}
        step={0.5}
        precision={2}
        helperText={t("validation_sentence", { min: 0.01, max: 25 })}
        onChange={onInput}
      />
      <NumberField
        name={kerbDistanceName}
        value={kerbDistance}
        label={`${t("Kerb distance")} (m)`}
        min={0}
        max={25}
        step={0.5}
        precision={2}
        helperText={t("validation_sentence", { min: 0, max: 25 })}
        tooltip={tooltip}
        onChange={onInput}
      />
    </Section>
  );
};

BorderArea.displayName = "BorderArea";

export default memo(BorderArea);
